import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { RootState } from '../../../reducers';
import { bindActionCreators, Dispatch } from 'redux';
import * as actions from '../../../actions';
import * as actionsJourney from '../../../actions/journey1';
import * as actionsJourney2 from '../../../actions/journey2';
import * as actionsJourney3 from '../../../actions/journey3';
import * as _actionsBasket from '../../../actions/basket';
import * as _actionsProduct from '../../../actions/product';
import { connect, useDispatch } from 'react-redux';
import { Button, EButtonType } from '@repo/apg-ui';
import { ReactComponent as RedxIcon } from '../../../assets/icons/redX.svg';
import classNames from 'classnames';
import './Digiflex.scss';
import Spinner from '../../Spinner';
import { prepareAddToBasketPayload } from '../../../utils/payloads';
import { createDateRange, parseToApiDate } from '../../../utils/react';
import DigiflexConfiguration from './DigiflexConfiguration';
import { getParameterByName } from '../../../utils/toSearchParams';
import { createBasket, updateBasketEntry } from '../../../utils/basket';
import { useNavigate } from 'react-router';
import ErrorContainer from '../../ErrorContainer/ErrorContainer';
import { isEmpty } from 'lodash';
import { clearError } from '../../../actions';
import { SchemaProduct } from '@repo/legacy-commerce-api';
import { PartiallyIntegratedUserDataEC } from '../../../reducers/app';

interface Props {
  history: any;
  intl: any;
  location: any;
  productsJ2ByUrn: Record<string, SchemaProduct>;
  possibleBookingDates: any;
  userDataEC: PartiallyIntegratedUserDataEC;
  actionsJourney2: any;
  actionsBasket: typeof _actionsBasket;
  actionsProduct: typeof _actionsProduct;
  productNew: any;
  industry: any;
  basketInfo: any;
  errors?: any;
  notices?: any;
}

export interface DigiflexProduct {
  name: string;
  state: number;
  timeslotsinformation: any;
  producturn: string;
  startdate: any;
  pricefordates: any;
  timeslot: any;
  baskettimeslot?: any;
  spotlength: any;
  basketspotlength?: any;
  shareoftime: any;
  basketshareoftime?: any;
  dailyrepetitions: any;
  dailytransmission: any;
  totaltransmission: any;
}

const DigiflexModal = ({
  location,
  productsJ2ByUrn,
  possibleBookingDates,
  userDataEC,
  intl,
  actionsJourney2,
  actionsBasket,
  industry,
  basketInfo,
  actionsProduct,
  errors,
  notices,
}: Props) => {
  const { digiflex, ui, basketProducts } = userDataEC;
  const dispatch = useDispatch();
  const urn = getParameterByName('urn', location.search);
  const _basketKey = getParameterByName('basketkey', location.search);

  const allBasketentries = basketProducts.productsByUrnAll[urn];
  const navigate = useNavigate();
  const [isModalReady, setIsModalReady] = React.useState(false);

  /**
   * Setzt alle relevanten Daten für Digiflex basierend auf einem Produkt und dessen Warenkorbstatus.
   * @param product Das Produktobjekt, dessen Eigenschaften übertragen werden sollen. Kann null sein.
   * @param inBasket Ein Flag, das angibt, ob das Produkt im Warenkorb ist.
   */
  const updateDigiflexProductData = (product: DigiflexProduct | null, inBasket: boolean) => {
    const matchingTimeslotInfo = product?.timeslotsinformation?.find(
      (info: any) => info.timeslot === product.baskettimeslot,
    );

    actionsProduct.setDigiflexData({
      product: product,
      isProductInBasket: inBasket,
      startDate: product ? product.startdate : null,
      timeslot: product?.baskettimeslot || product?.timeslot || digiflex.timeslot,
      spotLength: product?.basketspotlength || product?.spotlength || digiflex.spotLength,
      dailyTransmission: product?.dailytransmission || digiflex.dailyTransmission,
      totalTransmission: product?.totaltransmission || digiflex.totalTransmission,
      dailyRepetitions: product.dailyrepetitions || digiflex.dailyRepetitions,
      shareOfTime: product?.basketshareoftime || product?.shareoftime || digiflex.shareOfTime,
      timeslotInfo: matchingTimeslotInfo || digiflex.timeslotInfo,
    });
  };

  // Scrollt nach oben, wenn Fehler vorhanden sind
  useEffect(() => {
    if (errors && errors.length > 0) {
      window.scrollTo(0, 0);
      if (!isEmpty(errors)) {
        dispatch(clearError(null));
      }
    }
  }, [errors]);

  useEffect(() => {
    const basketEntry = basketProducts.productsByUrnDate[_basketKey] ?? null;

    if (basketEntry) {
      // @ts-expect-error type mismatch between two version of a SchemaProduct here see all the || above....
      updateDigiflexProductData(basketEntry, true);
      setIsModalReady(true);
    } else {
      if (Object.keys(productsJ2ByUrn || {}).includes(urn)) {
        // @ts-expect-error Digiflex schema varies a bit from SchemaProduct, can't assign:
        updateDigiflexProductData(productsJ2ByUrn[urn], false);
      }
      setIsModalReady(true);
    }
  }, []);

  const addOrCreateBasket = () => {
    actionsProduct.setDigiflexData({
      isLoading: true,
    });
    if (userDataEC.basketInfo) {
      if (digiflex.isProductInBasket) {
        return doUpdateBasket(userDataEC.basketInfo);
      } else {
        return addToBasket(userDataEC.basketInfo);
      }
    } else {
      return createBasket(
        userDataEC,
        userDataEC.startDate || digiflex.product?.startdate || digiflex.startDate,
        actionsBasket,
      ).then((res) => {
        return addToBasket({
          basket: res.data.basket,
          basketreference: res.data.basketreference,
        });
      });
    }
  };

  const doUpdateBasket = (basketData) => {
    const { basket, basketreference } = basketData;

    const payload = prepareAddToBasketPayload(
      basket,
      basketreference,
      parseToApiDate(digiflex.startDate),
      digiflex.duration,
      digiflex.product.producturn,
      industry,
      digiflex.timeslot,
      digiflex.spotLength,
      digiflex.shareOfTime,
      digiflex.budget,
    );

    return updateBasketEntry(payload, userDataEC.digiflex.product.basketentryid, actionsBasket)
      .then((res) => {
        if (res) {
          actionsBasket
            .getBasketContent(basketData)
            .then((res) => {
              actionsProduct.setDigiflexData({
                isLoading: false,
              });
              closeModal();
            })
            .catch((err) => {
              console.error(err);
              actionsProduct.setDigiflexData({
                isLoading: false,
              });
            });
        } else {
          actionsProduct.setDigiflexData({
            isLoading: false,
          });
        }
      })
      .catch((res) => {
        actionsProduct.setDigiflexData({
          isLoading: false,
        });
      });
  };

  const addToBasket = (basketData) => {
    const { basket, basketreference } = basketData;

    const payload = prepareAddToBasketPayload(
      basket,
      basketreference,
      parseToApiDate(digiflex.startDate),
      digiflex.duration,
      digiflex.product.producturn,
      industry,
      digiflex.timeslot,
      digiflex.spotLength,
      digiflex.shareOfTime,
      digiflex.budget,
    );

    return actionsBasket
      .addToBasket(payload)
      .then((res) => {
        actionsBasket
          .getBasketContent(basketData)
          .then((res) => {
            actionsProduct.setDigiflexData({
              isLoading: false,
            });
            closeModal();
          })
          .catch((err) => {
            console.error(err);
            actionsProduct.setDigiflexData({
              isLoading: false,
            });
          });
      })
      .catch((res) => {
        actionsProduct.setDigiflexData({
          isLoading: false,
        });
      });
  };

  const resetForm = () => {
    actionsProduct.setDigiflexData({
      duration: digiflex.initData.duration,
      timeslot: digiflex.initData.timeslot,
      budget: digiflex.initData.budget,
      dailyRepetitions: digiflex.initData.dailyrepetition,
      dailyTransmission: digiflex.initData.dailytransmission,
      totalTransmission: digiflex.initData.totaltransmission,
      spotLength: digiflex.initData.spotLength,
      startDate: digiflex.initData.startDate,
      isBudgetChanged: false,
      dateRange: [
        createDateRange(digiflex.initData.startDate, digiflex.initData.duration, {
          toEndOfYear: digiflex.initData.duration > 28,
          fullWeeks: false,
        }),
      ],
    });
  };

  const closeModal = () => {
    actionsProduct.clearDigiflexData();
    const redirect = getParameterByName('redirect', location.search);
    if (redirect === 'product') {
      navigate({ search: `?modalContent=product&urn=${digiflex.product.producturn}` });
    } else {
      navigate({ search: '' });
    }
  };

  // @ts-ignore
  return digiflex.product ? (
    <div
      className={classNames('digiflexModal', {
        modalLoading: digiflex.isLoading,
      })}
      id='root'
    >
      {digiflex.isLoading && <Spinner />}
      <div className={classNames('modalHeader d-flex justify-content-between align-items-start border-bottom h-auto')}>
        <div>
          <h3>
            <FormattedMessage id='ec.modals.digiflex.customise' defaultMessage='Anpassen' />
          </h3>
          {(errors || notices) && (
            <ErrorContainer
              getDataFromRedux={true}
              journeyType={'journey02'}
              classNameErrorWrapper={'mw-100 m-0 mt-3'}
              classNameErrorContainer={'m-0'}
            />
          )}
          <div>{digiflex.product.name}</div>
          <div className='font-14 mt-3'>
            <FormattedMessage
              id='ec.modals.digiflex.infoMessage'
              defaultMessage='Individualisieren Sie hier die Anzahl Ausstrahlungen Ihres Werbespots.
                        Markieren Sie dazu im Kalender die gewünschte Laufzeit (Anz. Tage) passen
                        Sie Ihr Budget und/oder die Spotlänge nach Ihren Bedürfnissen an.'
            />
          </div>
        </div>
        <div className='close-modal align-self-start'>
          <Button onClick={closeModal} className='mt-0'>
            <RedxIcon />
          </Button>
        </div>
      </div>
      <div className={classNames('modalBody h-100')}>
        {isModalReady && (
          <DigiflexConfiguration
            intl={intl}
            location={location}
            productsJ2ByUrn={productsJ2ByUrn}
            possibleBookingDates={possibleBookingDates}
            userDataEC={userDataEC}
            actionsJourney2={actionsJourney2}
            actionsBasket={actionsBasket}
            industry={industry}
            basketInfo={basketInfo}
            setDigiflexData={actionsProduct.setDigiflexData}
          />
        )}
      </div>
      <div
        className={classNames('modalFooter border-top d-flex w-100 h-auto bg-white', {
          'justify-content-between': !ui.isMobile,
          'justify-content-center': ui.isMobile,
        })}
      >
        <Button buttonType={EButtonType.BasicLink} className='mt-0 me-24' onClick={resetForm}>
          <FormattedMessage id='ec.components.buttons.reset' defaultMessage='Reset' />
        </Button>
        <div className='d-flex align-items-center'>
          <Button
            buttonType={EButtonType.InvertedPillMedium}
            className='mt-0'
            isDisabled={errors.length > 0 || digiflex.invalidDaysInRange.length === digiflex.duration}
            onClick={() => {
              addOrCreateBasket();
            }}
          >
            {digiflex.isProductInBasket ? (
              <FormattedMessage id='ec.components.buttons.updateInBasket' defaultMessage='Update' />
            ) : (
              <FormattedMessage id='ec.components.buttons.addToBasket' defaultMessage='Zum Warenkorb hinzufügen' />
            )}
          </Button>
          <Button buttonType={EButtonType.BasicLink} className='mt-0 ms-24' onClick={closeModal}>
            <FormattedMessage id='ec.components.buttons.cancel' defaultMessage='Abbrechen' />
          </Button>
        </div>
      </div>
    </div>
  ) : (
    <React.Fragment />
  );
};

function mapStateToProps(state: RootState, ownProps: any) {
  return {
    ...ownProps,
    ...state.app,
    ...state.router,
    location: state.router.location,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    actions: bindActionCreators(actions as any, dispatch),
    actionsJourney: bindActionCreators(actionsJourney as any, dispatch),
    actionsJourney2: bindActionCreators(actionsJourney2 as any, dispatch),
    actionsJourney3: bindActionCreators(actionsJourney3 as any, dispatch),
    actionsBasket: bindActionCreators(_actionsBasket as any, dispatch),
    actionsProduct: bindActionCreators(_actionsProduct as any, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DigiflexModal);
